import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import showNotification, {
	isDuplicateNotification,
	TNotificationType,
} from '../components/extras/showNotification';
import { resolveNotificationRoute } from '../helpers/notificationRouteResolver';
import { ENV } from '../config/env.config';
import { apiClient } from './apiClient';
import { AUTH_ENDPOINTS } from '../constants/apiEndpoints';

const firebaseConfig = {
	apiKey: ENV.FIREBASE.API_KEY,
	authDomain: ENV.FIREBASE.AUTH_DOMAIN,
	projectId: ENV.FIREBASE.PROJECT_ID,
	storageBucket: ENV.FIREBASE.STORAGE_BUCKET,
	messagingSenderId: ENV.FIREBASE.MESSAGING_SENDER_ID,
	appId: ENV.FIREBASE.APP_ID || '1:675127168107:web:096840c8c4d08e24ff752e',
};

// Initialize Firebase App instance safely (singleton)
const getFirebaseApp = () => {
	if (!getApps().length) {
		return initializeApp(firebaseConfig);
	}
	return getApp();
};

let isForegroundListenerAttached = false;

// Check if valid Web Firebase credentials are provided
export const isFirebaseWebConfigured = (): boolean => {
	const appId = ENV.FIREBASE.APP_ID;
	const vapidKey = ENV.FIREBASE.VAPID_KEY;

	// Google rejects ':android:' app IDs from web browsers with 400 Invalid Argument
	const hasValidWebAppId = Boolean(
		appId && appId.includes(':web:') && !appId.includes('YOUR_APP_ID'),
	);
	const hasValidVapidKey = Boolean(vapidKey && !vapidKey.includes('YOUR_PUBLIC_VAPID_KEY'));

	return hasValidWebAppId && hasValidVapidKey;
};

export const getMessagingInstance = (): Messaging | null => {
	if (!isFirebaseWebConfigured()) {
		return null;
	}

	if (typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator) {
		try {
			const app = getFirebaseApp();
			return getMessaging(app);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error initializing Firebase Messaging:', error);
			return null;
		}
	}
	return null;
};

// Persistent device identifier stored in localStorage
export const getDeviceId = (): string => {
	let deviceId = localStorage.getItem('device_id');
	if (!deviceId) {
		const randomPart =
			typeof crypto !== 'undefined' && crypto.randomUUID
				? crypto.randomUUID()
				: Math.random().toString(36).substring(2, 15);
		deviceId = `web-${randomPart}`;
		localStorage.setItem('device_id', deviceId);
	}
	return deviceId;
};

// Sync FCM token with backend server
export const syncTokenWithBackend = async (
	fcmToken: string,
	authToken?: string,
): Promise<boolean> => {
	try {
		await apiClient(AUTH_ENDPOINTS.FCM_TOKEN, {
			body: {
				device_id: getDeviceId(),
				fcm_token: fcmToken,
			},
			...(authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
		});
		console.log('✅ FCM token successfully registered with backend');
		return true;
	} catch (error) {
		console.error('❌ Failed to sync FCM token with backend:', error);
		return false;
	}
};

// Attach foreground listener immediately so push notifications are never missed
const attachForegroundListener = (messagingInstance: Messaging) => {
	if (isForegroundListenerAttached) return;
	isForegroundListenerAttached = true;

	onMessage(messagingInstance, (payload) => {
		console.log('🔔 [Foreground] FCM Push received in tab:', payload);
		const title =
			payload.notification?.title || payload.data?.title || 'StichMaster Notification';
		const body = payload.notification?.body || payload.data?.body || '';
		const rawType = String(payload.data?.type || '').toLowerCase();
		const notifId = payload.data?.id || payload.messageId || '';

		// Deduplicate (prevents double firing)
		const dedupKey = `${notifId}:${title}:${body}`;
		if (isDuplicateNotification(dedupKey)) {
			return;
		}

		let notifType: TNotificationType = 'info';
		if (
			rawType.includes('success') ||
			rawType.includes('complete') ||
			rawType.includes('approved')
		) {
			notifType = 'success';
		} else if (rawType.includes('warn') || rawType.includes('pending')) {
			notifType = 'warning';
		} else if (
			rawType.includes('error') ||
			rawType.includes('danger') ||
			rawType.includes('reject')
		) {
			notifType = 'danger';
		}

		const resolved =
			resolveNotificationRoute({
				...payload.data,
				...(payload.notification || {}),
				data: payload.data,
			}) || payload.data?.url;
		const targetUrl = resolved && resolved !== '/' ? resolved : undefined;
		const handleClick = targetUrl
			? () => {
					if (typeof window !== 'undefined') {
						if (window.location.pathname === targetUrl) {
							window.location.reload();
						} else {
							window.location.href = targetUrl;
						}
					}
			  }
			: undefined;

		// Render colorful, styled StichMaster notification card from bottom-right (Firebase Push)
		showNotification(title, body, notifType, handleClick, 'bottom-right');

		// Dispatch event so notification counters in header update in real-time
		if (typeof window !== 'undefined') {
			window.dispatchEvent(
				new CustomEvent('stichmaster:new_notification', {
					detail: {
						title,
						message: body,
						type: notifType,
						data: payload.data,
					},
				}),
			);
		}
	});
};

// Request user permission and initialize push notifications
export const initPushNotifications = async (authToken?: string): Promise<string | null> => {
	if (
		typeof window === 'undefined' ||
		!('Notification' in window) ||
		!('serviceWorker' in navigator)
	) {
		return null;
	}

	// Avoid running or triggering network calls if Web keys are not yet configured
	if (!isFirebaseWebConfigured()) {
		// eslint-disable-next-line no-console
		console.info(
			'ℹ️ Push notifications idle: awaiting Firebase Web App ID (:web:) and VAPID key in .env',
		);
		return null;
	}

	const vapidKey = ENV.FIREBASE.VAPID_KEY;

	const messagingInstance = getMessagingInstance();
	if (!messagingInstance) {
		return null;
	}

	// Attach foreground listener immediately
	attachForegroundListener(messagingInstance);

	try {
		const permission = await Notification.requestPermission();
		if (permission !== 'granted') {
			return null;
		}

		// Pass ENV configuration dynamically to Service Worker via query parameters
		const swUrl = `/firebase-messaging-sw.js?apiKey=${encodeURIComponent(
			firebaseConfig.apiKey || '',
		)}&authDomain=${encodeURIComponent(
			firebaseConfig.authDomain || '',
		)}&projectId=${encodeURIComponent(
			firebaseConfig.projectId || '',
		)}&storageBucket=${encodeURIComponent(
			firebaseConfig.storageBucket || '',
		)}&messagingSenderId=${encodeURIComponent(
			firebaseConfig.messagingSenderId || '',
		)}&appId=${encodeURIComponent(firebaseConfig.appId || '')}`;

		// Register service worker from public directory and ensure it updates
		const registration = await navigator.serviceWorker.register(swUrl, {
			updateViaCache: 'none',
		});
		await registration.update();

		// Retrieve FCM registration token
		const token = await getToken(messagingInstance, {
			serviceWorkerRegistration: registration,
			vapidKey,
		});

		if (token) {
			// eslint-disable-next-line no-console
			console.log('🔑 Active Web FCM Token:', token);
			await syncTokenWithBackend(token, authToken);
		}

		return token;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error initializing push notifications:', error);
		return null;
	}
};

export default {
	getDeviceId,
	syncTokenWithBackend,
	initPushNotifications,
};

if (typeof window !== 'undefined') {
	(window as any).__testNotification = (title?: string, body?: string) => {
		showNotification(
			title || 'Test Notification',
			body || 'This is a test notification from browser console',
			'success',
			undefined,
			'bottom-right',
		);
	};

	(window as any).__firebasePushDiagnostics = async () => {
		const registrations = await navigator.serviceWorker.getRegistrations();
		const messagingRegistrations = registrations
			.map((registration) => ({
				scope: registration.scope,
				active: registration.active?.scriptURL || null,
				state: registration.active?.state || null,
			}))
			.filter((registration) => registration.active?.includes('firebase-messaging-sw'));

		const messaging = getMessagingInstance();
		let token: string | null = null;
		let tokenError: string | null = null;
		try {
			if (messaging) {
				const firebaseRegistration = registrations.find((registration) =>
					registration.active?.scriptURL.includes('firebase-messaging-sw'),
				);
				if (firebaseRegistration) {
					token = await getToken(messaging, {
						serviceWorkerRegistration: firebaseRegistration,
						vapidKey: ENV.FIREBASE.VAPID_KEY,
					});
				}
			}
		} catch (error) {
			tokenError = error instanceof Error ? error.message : String(error);
		}

		const result = {
			permission: typeof Notification === 'undefined' ? 'unavailable' : Notification.permission,
			secureContext: window.isSecureContext,
			serviceWorkers: messagingRegistrations,
			pageController: navigator.serviceWorker.controller?.scriptURL || null,
			token,
			tokenError,
		};
		console.log('Firebase push diagnostics:', result);
		return result;
	};
}
